import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError, setToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (active) setUser(data.user || false);
      } catch {
        if (active) setUser(false);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  async function login(email, password) {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      // Network failure (no response from server) — give the user a clear message
      if (!e.response) {
        return {
          ok: false,
          error:
            "Cannot reach the server. Check your internet connection or try again in a moment.",
        };
      }
      const detail = e.response?.data?.detail;
      // Server replied without a detail field — fall back to status code
      if (detail == null) {
        if (e.response.status === 401) return { ok: false, error: "Invalid email or password." };
        if (e.response.status === 429) return { ok: false, error: "Too many failed attempts. Please wait a few minutes and try again." };
        if (e.response.status >= 500) return { ok: false, error: "Server error. Please try again in a moment." };
        return { ok: false, error: `Sign-in failed (status ${e.response.status}).` };
      }
      return { ok: false, error: formatApiError(detail) };
    }
  }

  async function register(payload) {
    try {
      const { data } = await api.post("/auth/register", payload);
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      if (!e.response) {
        return {
          ok: false,
          error: "Cannot reach the server. Check your internet connection or try again.",
        };
      }
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch {}
    setToken("");
    setUser(false);
  }

  function setAuth({ access_token, user: u }) {
    if (access_token) setToken(access_token);
    if (u) setUser(u);
  }

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, setUser, setAuth }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
